import { Event } from "@/types/event";
import { supabase } from "@/integrations/supabase/client";
import { getPurchaseAttributionBundle } from "@/lib/lastTouchAttribution";

interface EventPaymentHandlerProps {
  event: Event;
  seats: number;
  onSuccess: (paymentMethod: string) => void;
  /** Second arg carries the checkout-call status so callers can tell a
   * capacity refusal (409 — nobody was charged) from a payment failure. */
  onError: (message?: string, opts?: { status?: number }) => void;
  referralCode?: string | null;
  specialOfferCode?: string | null;
  applyCredits?: boolean;
  urlPartnerCode?: string | null;
  forFriend?: boolean;
  joinMembership?: boolean;
  groupId?: string | null;
}

export const handlePayment = async ({
  event,
  seats,
  onSuccess,
  onError,
  referralCode,
  specialOfferCode,
  applyCredits = true,
  urlPartnerCode,
  forFriend,
  joinMembership,
  groupId,
}: EventPaymentHandlerProps) => {
  try {
    // Get the current session
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session) {
      console.error("No active session found");
      onError();
      return;
    }

    const attribution = getPurchaseAttributionBundle();

    const { data, error } = await supabase.functions.invoke("create-checkout", {
      body: {
        seats,
        eventId: event.id,
        eventTitle: event.title,
        unitPrice: event.price,
        referralCode,
        specialOfferCode,
        applyCredits,
        urlPartnerCode,
        forFriend,
        joinMembership: !!joinMembership,
        groupId,
        ...attribution,
      },
    });

    if (error) {
      console.error("Error creating checkout session:", error);
      let message: string | undefined;
      const status = (error as any).context?.status;
      try {
        const body = await (error as any).context?.json?.();
        message = body?.error;
      } catch {
        // ignore unreadable error body
      }
      onError(message, { status });
      return;
    }

    if (data?.clientSecret) {
      // Navigate to embedded checkout page
      const params = new URLSearchParams({
        clientSecret: data.clientSecret,
        eventId: event.id,
      });
      window.location.href = `/checkout?${params.toString()}`;
    } else {
      console.error("No checkout client secret received");
      onError();
    }
  } catch (err) {
    console.error("Payment processing error:", err);
    onError();
  }
};
